import { useQuery } from '@tanstack/react-query'
import { Suspense, useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router'

import { endpoints } from '../api/endpoints'
import { useSession } from '../auth/context'
import { useCatalogMode, useEmenuVisible } from '../catalogMode'
import { LegalLinks } from '../pages/LegalPages'
import { SubscriptionBanner } from '../pages/SubscriptionPage'
import { t } from '../strings'
import { Icon, type IconName } from './IconV2'

/** `also`: pages reached from this one, where it stays highlighted (history and password are opened from Info outlet). */
type NavItem = { to: string; label: string; icon: IconName; end?: boolean; also?: string[] }
type NavGroup = { title: string; items: NavItem[] }

const navGroups = (dataLabel: string, emenu: boolean): NavGroup[] => [
  {
    title: t.navGroupOverview,
    items: [
      { to: '/', label: t.navDashboard, icon: 'dashboard', end: true },
      { to: '/laporan', label: t.navHome, icon: 'reports' },
      { to: '/transaksi', label: t.navSales, icon: 'sales' },
    ],
  },
  {
    title: t.navGroupCatalog,
    items: [
      { to: '/data', label: dataLabel, icon: 'items' },
      { to: '/hp-kasir', label: t.navDevices, icon: 'devices' },
    ],
  },
  // Same order as the cashier app's Pengaturan group: outlet, staff, cashier settings, receipt, history, subscription.
  {
    title: t.navGroupSettings,
    items: [
      { to: '/pengaturan', label: t.outletInfo, icon: 'business', end: true, also: ['/pengaturan/riwayat', '/pengaturan/kata-sandi', '/pengaturan/hapus-akun', '/pengaturan/jenis-usaha'] },
      { to: '/pengaturan/pengguna', label: t.usersLink, icon: 'users' },
      { to: '/pengaturan/kasir', label: t.kasirLink, icon: 'kasir' },
      ...(emenu ? [{ to: '/pengaturan/e-menu', label: t.emenuTitle, icon: 'items' as const }] : []),
      { to: '/pengaturan/struk', label: t.layoutTitle, icon: 'receipt' },
      { to: '/langganan', label: t.navSubscription, icon: 'subscription' },
    ],
  },
]

const under = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`)
const isOn = (i: NavItem, pathname: string) => (i.end ? pathname === i.to : under(pathname, i.to)) || !!i.also?.some((a) => under(pathname, a))

/** The frame of every signed-in page: a slide-in drawer on a phone, a fixed sidebar from 1024 px. */
export function Layout() {
  const { logout } = useSession()
  const { pathname } = useLocation()
  const biz = useQuery({ queryKey: ['business'], queryFn: endpoints.business })
  const name = biz.data?.name ?? ''
  const { words, loaded } = useCatalogMode()
  const emenu = useEmenuVisible()
  const groups = navGroups(loaded ? words.nav : t.loading, emenu)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    if (!drawerOpen) return
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setDrawerOpen(false)
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [drawerOpen])

  const all = groups.flatMap((g) => g.items)
  const active = [...all].sort((a, b) => b.to.length - a.to.length).find((i) => isOn(i, pathname)) ?? all[0]!

  return (
    <div className="app-shell">
      <a className="skip" href="#isi">
        {t.skipToContent}
      </a>
      <header className="mobile-topbar">
        <button type="button" className="drawer-trigger-btn" aria-label={t.openMenu} aria-expanded={drawerOpen} aria-controls="owner-navigation-drawer" onClick={() => setDrawerOpen(true)}>
          <Icon name="menu" size={22} />
        </button>
        <div className="mobile-topbar-titles">
          <span className="mobile-topbar-biz">{name || t.appName}</span>
          <span className="mobile-topbar-page">{active.label}</span>
        </div>
      </header>

      {drawerOpen && <div className="drawer-backdrop" role="presentation" onClick={() => setDrawerOpen(false)} />}

      <aside id="owner-navigation-drawer" className={`sidebar-drawer ${drawerOpen ? 'is-open' : ''}`.trim()}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-left">
            <img src="/logo-mark.webp" alt="" width={34} height={36} className="brand-logo-img" />
            <div className="sidebar-brand-text">
              <strong className="brand-title">{name || t.loading}</strong>
              <span className="brand-subtitle">{biz.data?.outlet.name ?? t.appName}</span>
            </div>
          </div>
          <button type="button" className="drawer-close-btn" aria-label={t.closeMenu} onClick={() => setDrawerOpen(false)}>
            <Icon name="close" size={20} />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label={t.menuMain}>
          {groups.map((group) => (
            <div key={group.title} className="nav-group">
              <div className="nav-group-label">{group.title}</div>
              <ul className="nav-group-list">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={() => setDrawerOpen(false)}
                      className={`nav-link ${isOn(item, pathname) ? 'active' : ''}`.trim()}
                      aria-current={isOn(item, pathname) ? 'page' : undefined}
                    >
                      <Icon name={item.icon} size={19} />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user-chip">
            <div className="sidebar-user-avatar" aria-hidden="true">
              {(name.trim()[0] ?? 'G').toUpperCase()}
            </div>
            <div className="sidebar-user-meta">
              <span className="sidebar-user-name">{name || t.appName}</span>
              <span className="sidebar-user-role">Akses Pemilik</span>
            </div>
          </div>
          <button type="button" className="btn btn-ghost logout-btn" onClick={() => void logout()}>
            <Icon name="logout" size={18} />
            <span>{t.logout}</span>
          </button>
        </div>
      </aside>

      <div className="shell-main-wrap">
        <main className="shell-content" id="isi" tabIndex={-1}>
          <SubscriptionBanner />
          <Suspense fallback={<p className="muted">{t.loading}</p>}>
            <Outlet />
          </Suspense>
          <LegalLinks exclude={['data']} />
        </main>
      </div>
    </div>
  )
}
